import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = "May 11, 2026";

function Terms() {
  return (
    <div className="bg-white min-h-screen">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-line">
        <div className="max-w-[1560px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-bold text-[15px] text-ink">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-xs font-extrabold">Q</span>
            <span>QuickBook Connector</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-muted px-3.5 py-2 border border-line rounded-lg hover:text-ink hover:bg-soft transition-all">
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-6 py-16 pb-24">
        <span className="inline-block text-xs font-semibold tracking-wider uppercase text-indigo mb-3.5">Legal</span>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-4 text-ink">Terms of Service</h1>
        <div className="flex flex-wrap gap-3.5 items-center text-sm text-muted mb-9">
          <span className="px-3 py-1.5 border border-line rounded-full bg-soft">Last updated: {EFFECTIVE_DATE}</span>
          <span>Effective: {EFFECTIVE_DATE}</span>
        </div>

        <p className="text-[17px] text-muted p-5 bg-soft border-l-[3px] border-navy rounded-lg mb-12">
          These Terms of Service ("Terms") govern your access to and use of <strong>QuickBook Connector</strong>
          {" "}(the "Service") provided by <strong>XcelerateAI Inc.</strong> ("we", "us", or "our"). By using the
          Service, you agree to be bound by these Terms.
        </p>

        <nav className="bg-soft border border-line rounded-xl p-6 mb-12" aria-label="Table of contents">
          <h4 className="m-0 mb-2.5 text-[13px] font-semibold tracking-wider uppercase text-muted">Contents</h4>
          <ol className="m-0 pl-5.5 text-ink text-[14.5px] space-y-1">
            <li><a href="#s1" className="text-ink hover:text-indigo hover:underline">Acceptance of Terms</a></li>
            <li><a href="#s2" className="text-ink hover:text-indigo hover:underline">Description of Service</a></li>
            <li><a href="#s3" className="text-ink hover:text-indigo hover:underline">Accounts & Eligibility</a></li>
            <li><a href="#s4" className="text-ink hover:text-indigo hover:underline">Acceptable Use</a></li>
            <li><a href="#s5" className="text-ink hover:text-indigo hover:underline">Third-Party Services</a></li>
            <li><a href="#s6" className="text-ink hover:text-indigo hover:underline">Intellectual Property</a></li>
            <li><a href="#s7" className="text-ink hover:text-indigo hover:underline">Disclaimers</a></li>
            <li><a href="#s8" className="text-ink hover:text-indigo hover:underline">Limitation of Liability</a></li>
            <li><a href="#s9" className="text-ink hover:text-indigo hover:underline">Termination</a></li>
            <li><a href="#s10" className="text-ink hover:text-indigo hover:underline">Changes to These Terms</a></li>
            <li><a href="#s11" className="text-ink hover:text-indigo hover:underline">Governing Law</a></li>
            <li><a href="#s12" className="text-ink hover:text-indigo hover:underline">Contact</a></li>
          </ol>
        </nav>

        <h2 id="s1" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink first:border-t-0 first:pt-0 first:mt-0">1. Acceptance of Terms</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          By accessing, installing, deploying, or using the Service, you confirm that you have read, understood,
          and agree to be bound by these Terms. If you are using the Service on behalf of an organization, you
          represent that you have authority to bind that organization to these Terms.
        </p>

        <h2 id="s2" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">2. Description of Service</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          QuickBook Connector is a developer middleware that enables applications to authenticate with
          QuickBooks Online via OAuth 2.0 and to perform accounting operations through the Intuit API. The
          Service is provided as software you deploy and operate; we do not host accounting data on your behalf
          unless explicitly stated otherwise.
        </p>

        <h2 id="s3" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">3. Accounts & Eligibility</h2>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li>You must be at least 18 years old or the age of majority in your jurisdiction.</li>
          <li>You are responsible for keeping your credentials, JWT secrets, and OAuth client secrets confidential.</li>
          <li>You are responsible for all activity that occurs under your account or deployment.</li>
          <li>You must promptly notify us of any unauthorized access or security breach.</li>
        </ul>

        <h2 id="s4" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">4. Acceptable Use</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">You agree NOT to use the Service to:</p>
        <ul className="mb-4 pl-5.5 text-[#1f2638] text-[15.5px] space-y-2">
          <li>Violate any applicable law, regulation, or third-party rights.</li>
          <li>Access QuickBooks data you are not authorized to access.</li>
          <li>Reverse engineer, attack, or attempt to disrupt the Service or Intuit's systems.</li>
          <li>Resell, sublicense, or misrepresent the Service as your own without permission.</li>
          <li>Send spam, malware, or otherwise harmful content through the Service.</li>
        </ul>

        <h2 id="s5" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">5. Third-Party Services</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          The Service integrates with <strong>QuickBooks Online</strong> and the Intuit Developer Platform. Your
          use of QuickBooks data is also governed by{" "}
          <a href="https://www.intuit.com/legal/terms/" target="_blank" rel="noreferrer" className="text-indigo underline underline-offset-2 hover:no-underline">Intuit's Terms of Service</a>.
          We are not responsible for the availability, accuracy, or behavior of third-party services.
        </p>

        <h2 id="s6" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">6. Intellectual Property</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          All rights, title, and interest in the Service — including its source code, trademarks, and
          documentation — remain the property of XcelerateAI Inc. and its licensors, except as expressly
          granted under an applicable open-source or commercial license. You retain all rights to data you
          submit or process through the Service.
        </p>

        <h2 id="s7" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">7. Disclaimers</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
          INCLUDING WITHOUT LIMITATION ANY WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
          NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, error-free, or secure.
        </p>

        <h2 id="s8" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">8. Limitation of Liability</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL XCELERATEAI BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE,
          DATA, OR GOODWILL ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL AGGREGATE LIABILITY
          WILL NOT EXCEED THE AMOUNT YOU PAID US (IF ANY) IN THE 12 MONTHS PRECEDING THE CLAIM.
        </p>

        <h2 id="s9" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">9. Termination</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          We may suspend or terminate your access to the Service at any time, with or without notice, for
          conduct that we believe violates these Terms or is harmful to other users, us, or third parties. You
          may stop using the Service at any time. Sections that by their nature should survive termination
          (including IP, disclaimers, and liability) will survive.
        </p>

        <h2 id="s10" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">10. Changes to These Terms</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          We may update these Terms from time to time. When we do, we will revise the "Last updated" date above.
          Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
        </p>

        <h2 id="s11" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">11. Governing Law</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">
          These Terms are governed by the laws of the jurisdiction in which XcelerateAI Inc. is established,
          without regard to its conflict-of-law principles. Any disputes will be resolved in the courts of that
          jurisdiction, unless otherwise required by applicable law.
        </p>

        <h2 id="s12" className="text-[22px] font-bold tracking-tight mt-12 mb-3.5 pt-7 border-t border-line text-ink">12. Contact</h2>
        <p className="mb-3.5 text-[#1f2638] text-[15.5px]">For questions about these Terms, contact us at:</p>
        <div className="mt-8 p-6 border border-line rounded-xl bg-white">
          <p className="my-1.5 text-[15px]"><strong>XcelerateAI Inc.</strong></p>
          <p className="my-1.5 text-[15px]">Email: <a href="mailto:support@xcelerateai.com" className="text-indigo underline underline-offset-2 hover:no-underline">support@xcelerateai.com</a></p>
        </div>
      </main>

      <footer className="border-t border-line bg-soft">
        <div className="max-w-[1160px] mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-xs font-extrabold">Q</span>
                <span className="font-bold text-[15px] text-ink">QuickBook Connector</span>
              </div>
              <p className="text-sm text-muted leading-relaxed">
                Developer middleware for seamless QuickBooks Online integration via OAuth 2.0.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-ink mb-3">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-sm text-muted hover:text-indigo transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm text-muted hover:text-indigo transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-ink mb-3">Contact</h3>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:support@xcelerateai.com" className="text-sm text-muted hover:text-indigo transition-colors">
                    support@xcelerateai.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-6 border-t border-line">
            <p className="text-sm text-muted text-center">
              © {new Date().getFullYear()} XcelerateAI Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Terms;
